import { ApiError } from "api/client";
import { type ContentReportCategory, type ContentReportPayload, submitContentReport } from "api/contentReports";
import { HoneypotField } from "components";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";
import { useSession } from "Session";

import { REPORT_CATEGORIES, type ReportTreeDialogProps, type ReportTreeStatus } from "./reportTreeDialog.types";
import ViewIcon from "./ViewIcon";

import styles from "./ReportTreeDialog.module.css";

/** Shortest details the backend accepts; checked here so the reason shows before the round trip. */
const MIN_DETAILS_LENGTH = 40;

/** Shortest good-faith statement the backend accepts. */
const MIN_STATEMENT_LENGTH = 20;

/**
 * "Report this tree" form. Open to anonymous visitors on purpose: a rights holder objecting to a
 * tree has no reason to hold an account. Name and email are collected only when signed out — a
 * signed-in complainant is identified by their token and the API rejects the fields if they are sent.
 */
const ReportTreeDialog = ({ onClose, treeId, treeTitle }: ReportTreeDialogProps) => {
	const { t } = useTranslation(["homePage", "common"]);
	const { hasSession, getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLDivElement>();

	const [category, setCategory] = useState<ContentReportCategory>("Copyright");
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [relationship, setRelationship] = useState("");
	const [details, setDetails] = useState("");
	const [statement, setStatement] = useState("");
	// Honeypot: hidden from real users, so a value here means a bot filled the form in.
	const [website, setWebsite] = useState("");
	const [status, setStatus] = useState<ReportTreeStatus>("idle");
	const [feedback, setFeedback] = useState("");

	const onCategoryChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setCategory(event.target.value as ContentReportCategory);
	}, []);

	const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setName(event.target.value);
	}, []);

	const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setEmail(event.target.value);
	}, []);

	const onRelationshipChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setRelationship(event.target.value);
	}, []);

	const onDetailsChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
		setDetails(event.target.value);
	}, []);

	const onStatementChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
		setStatement(event.target.value);
	}, []);

	const onWebsiteChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setWebsite(event.target.value);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			if (status === "submitting") {
				return;
			}

			const payload: ContentReportPayload = {
				treeId,
				category,
				claimedRelationship: relationship.trim(),
				details: details.trim(),
				goodFaithStatement: statement.trim()
			};

			if (!hasSession) {
				payload.name = name.trim();
				payload.email = email.trim();
			}

			if (payload.details.length < MIN_DETAILS_LENGTH) {
				setStatus("error");
				setFeedback(t("homePage:reportDialog.detailsTooShort", { min: MIN_DETAILS_LENGTH }));

				return;
			}

			if (payload.goodFaithStatement.length < MIN_STATEMENT_LENGTH) {
				setStatus("error");
				setFeedback(t("homePage:reportDialog.statementTooShort", { min: MIN_STATEMENT_LENGTH }));

				return;
			}

			// Only sent when a bot has filled the hidden field; the backend discards those submissions.
			if (website) {
				payload.website = website;
			}

			setStatus("submitting");
			setFeedback("");

			try {
				const token = hasSession ? await getAccessToken() : undefined;
				await submitContentReport(payload, token);
				setStatus("success");
			} catch (error) {
				setStatus("error");
				setFeedback(error instanceof ApiError ? error.message : t("common:genericError"));
			}
		},
		[
			category,
			details,
			email,
			getAccessToken,
			hasSession,
			name,
			relationship,
			statement,
			status,
			t,
			treeId,
			website
		]
	);

	const isSubmitting = status === "submitting";

	return (
		<>
			<button
				aria-label={t("homePage:reportDialog.close")}
				className={styles.scrim}
				onClick={onClose}
				type="button"
			/>
			<div
				ref={dialogRef}
				aria-label={t("homePage:reportDialog.title")}
				aria-modal="true"
				className={styles.dialog}
				role="dialog">
				<header className={styles.header}>
					<div className={styles.headerCopy}>
						<h2 className={styles.heading}>{t("homePage:reportDialog.title")}</h2>
						<p className={styles.subheading}>{t("homePage:reportDialog.subtitle", { title: treeTitle })}</p>
					</div>
					<button
						aria-label={t("homePage:reportDialog.close")}
						className={styles.closeButton}
						onClick={onClose}
						type="button">
						<ViewIcon name="close" />
					</button>
				</header>

				{status === "success" ? (
					<div className={styles.confirmation}>
						<p className={styles.success} role="status">
							{t("homePage:reportDialog.success")}
						</p>
						<button className={styles.submit} onClick={onClose} type="button">
							{t("homePage:reportDialog.done")}
						</button>
					</div>
				) : (
					<form className={styles.form} onSubmit={onSubmit}>
						<p className={styles.policyNote}>{t("homePage:reportDialog.policyNote")}</p>
						<Link className={styles.policyLink} target="_blank" to={Paths.rightsPolicy}>
							{t("homePage:reportDialog.policyLink")}
						</Link>

						<label className={styles.field} htmlFor="report-category">
							<span>{t("homePage:reportDialog.categoryLabel")}</span>
							<select
								disabled={isSubmitting}
								id="report-category"
								onChange={onCategoryChange}
								value={category}>
								{REPORT_CATEGORIES.map((option: ContentReportCategory) => (
									<option key={option} value={option}>
										{t(`homePage:reportDialog.categories.${option}`)}
									</option>
								))}
							</select>
						</label>

						{hasSession ? (
							<p className={styles.signedInNote}>{t("homePage:reportDialog.signedInNote")}</p>
						) : (
							<div className={styles.row}>
								<label className={styles.field} htmlFor="report-name">
									<span>{t("homePage:reportDialog.nameLabel")}</span>
									<input
										disabled={isSubmitting}
										id="report-name"
										maxLength={100}
										onChange={onNameChange}
										type="text"
										value={name}
										required
									/>
								</label>
								<label className={styles.field} htmlFor="report-email">
									<span>{t("homePage:reportDialog.emailLabel")}</span>
									<input
										disabled={isSubmitting}
										id="report-email"
										maxLength={256}
										onChange={onEmailChange}
										type="email"
										value={email}
										required
									/>
								</label>
							</div>
						)}

						<label className={styles.field} htmlFor="report-relationship">
							<span>{t("homePage:reportDialog.relationshipLabel")}</span>
							<input
								disabled={isSubmitting}
								id="report-relationship"
								maxLength={200}
								onChange={onRelationshipChange}
								placeholder={t("homePage:reportDialog.relationshipPlaceholder")}
								type="text"
								value={relationship}
								required
							/>
						</label>

						<label className={styles.field} htmlFor="report-details">
							<span>{t("homePage:reportDialog.detailsLabel")}</span>
							<textarea
								disabled={isSubmitting}
								id="report-details"
								maxLength={4000}
								onChange={onDetailsChange}
								placeholder={t("homePage:reportDialog.detailsPlaceholder")}
								rows={5}
								value={details}
								required
							/>
						</label>

						<label className={styles.field} htmlFor="report-statement">
							<span>{t("homePage:reportDialog.statementLabel")}</span>
							<textarea
								disabled={isSubmitting}
								id="report-statement"
								maxLength={1000}
								onChange={onStatementChange}
								placeholder={t("homePage:reportDialog.statementPlaceholder")}
								rows={3}
								value={statement}
								required
							/>
						</label>

						<HoneypotField id="report-website" onChange={onWebsiteChange} value={website} />

						<button className={styles.submit} disabled={isSubmitting} type="submit">
							{isSubmitting ? t("homePage:reportDialog.submitting") : t("homePage:reportDialog.submit")}
						</button>

						{feedback ? (
							<p className={styles.error} role="alert">
								{feedback}
							</p>
						) : null}
					</form>
				)}
			</div>
		</>
	);
};

export default ReportTreeDialog;
